import { useUser } from "@/api/hooks/user";
import type { LocationGroup } from "@/locationTable/types";
import type { AdvancedThreshold, ThresholdColor } from "@/overlay/components/CommonParts/Threshold/types";
import { useAppDispatch } from "@/reducer";
import {
  type LocationTableState,
  triggerLocationTableExport,
  triggerLocationTablePdfExport,
  updateLocationTableState,
} from "@/reducer/LocationTablesState";
import { getFeatureValue } from "@/types/featureSet";
import { useCallback } from "react";

export const useLocationTableHooks = (locationTable: LocationTableState) => {
  const dispatch = useAppDispatch();
  const { user, features } = useUser();
  const hasExport = !!getFeatureValue(features, "ui.csv_export");
  const exportAllowed = user?.is_developer || user?.is_superuser || hasExport;

  const handleTitleChange = useCallback(
    (title: string) => {
      dispatch(
        updateLocationTableState({
          locationTable: {
            id: locationTable.id,
            tabId: locationTable.tabId ? locationTable.tabId : undefined,
            title,
          },
          id: locationTable.id,
        }),
      );
    },
    [dispatch, locationTable],
  );

  const handleGroupsChange = useCallback(
    (groups: LocationGroup[]) => {
      dispatch(
        updateLocationTableState({
          locationTable: {
            id: locationTable.id,
            tabId: locationTable.tabId ? locationTable.tabId : undefined,
            groups,
          },
          id: locationTable.id,
        }),
      );
    },
    [dispatch, locationTable],
  );

  const handleTableChange = useCallback(
    (table: Partial<LocationTableState>) => {
      dispatch(
        updateLocationTableState({
          locationTable: {
            id: locationTable.id,
            tabId: locationTable.tabId ? locationTable.tabId : undefined,
            ...table,
          },
          id: locationTable.id,
        }),
      );
    },
    [dispatch, locationTable],
  );

  const handleDateTimeRangeChange = useCallback(
    ([from, to]: [string | null, string | null], resolution: string | null) => {
      dispatch(
        updateLocationTableState({
          locationTable: {
            id: locationTable.id,
            datetimeFrom: from ? from : undefined,
            datetimeTo: to ? to : undefined,
            resolution: resolution ? resolution : undefined,
            tabId: locationTable.tabId ? locationTable.tabId : undefined,
          },
          id: locationTable.id,
        }),
      );
    },
    [dispatch, locationTable],
  );

  const handleParamChange = useCallback(
    (param: string) => {
      dispatch(
        updateLocationTableState({
          locationTable: {
            id: locationTable.id,
            tabId: locationTable.tabId ? locationTable.tabId : undefined,
            parameterUnit: param,
          },
          id: locationTable.id,
        }),
      );
    },
    [dispatch, locationTable],
  );

  const handleModelChange = useCallback(
    (param: string) => {
      dispatch(
        updateLocationTableState({
          locationTable: {
            id: locationTable.id,
            tabId: locationTable.tabId ? locationTable.tabId : undefined,
            model: param,
          },
          id: locationTable.id,
        }),
      );
    },
    [dispatch, locationTable],
  );

  const handleThresholdsChange = useCallback(
    (thresholds?: ThresholdColor[]) => {
      dispatch(
        updateLocationTableState({
          locationTable: {
            id: locationTable.id,
            tabId: locationTable.tabId ? locationTable.tabId : undefined,
            thresholds: thresholds || [],
          },
          id: locationTable.id,
        }),
      );
    },
    [dispatch, locationTable],
  );

  const handleAdvancedThresholdsChange = useCallback(
    (thresholds?: AdvancedThreshold[]) => {
      dispatch(
        updateLocationTableState({
          locationTable: {
            id: locationTable.id,
            tabId: locationTable.tabId ? locationTable.tabId : undefined,
            advancedThreshold: thresholds,
          },
          id: locationTable.id,
        }),
      );
    },
    [dispatch, locationTable],
  );

  const handleColumnGroupingChange = useCallback(
    (param: boolean) => {
      dispatch(
        updateLocationTableState({
          locationTable: {
            id: locationTable.id,
            tabId: locationTable.tabId ? locationTable.tabId : undefined,
            areColumnsGrouped: param,
          },
          id: locationTable.id,
        }),
      );
    },
    [dispatch, locationTable],
  );

  const handleRowGroupingChange = useCallback(
    (param: boolean) => {
      dispatch(
        updateLocationTableState({
          locationTable: {
            id: locationTable.id,
            tabId: locationTable.tabId ? locationTable.tabId : undefined,
            areRowsGrouped: param,
          },
          id: locationTable.id,
        }),
      );
    },
    [dispatch, locationTable],
  );

  const handleCSVExport = useCallback(() => {
    dispatch(
      triggerLocationTableExport({
        id: locationTable.id,
      }),
    );
  }, [dispatch, locationTable]);

  const handlePDFExport = useCallback(() => {
    dispatch(
      triggerLocationTablePdfExport({
        id: locationTable.id,
      }),
    );
  }, [dispatch, locationTable]);

  return {
    handleTitleChange,
    handleDateTimeRangeChange,
    handleParamChange,
    handleGroupsChange,
    handleColumnGroupingChange,
    handleRowGroupingChange,
    handleModelChange,
    handleThresholdsChange,
    handleAdvancedThresholdsChange,
    handleCSVExport,
    handlePDFExport,
    handleTableChange,
    exportAllowed,
  };
};
